import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Zap, Flame, Award, UserX, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function Portfolio() {
  const { id } = useParams();
  const { getPublicProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) { setNotFound(true); setLoading(false); return; }
    getPublicProfile(id).then(data => {
      if (!data) setNotFound(true);
      else setProfile(data);
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [id, getPublicProfile]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff' }}>
      <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading portfolio...</div>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: '#ffffff', padding: '2rem' }}>
      <UserX size={64} color="rgba(15, 23, 42, 0.15)" style={{ marginBottom: '1rem' }} />
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--navy-dark)', marginBottom: '0.5rem' }}>Scholar Not Found</h2>
      <p style={{ color: 'var(--text-slate)', marginBottom: '2rem' }}>The requested achievement portfolio does not exist or is private.</p>
      <Link to="/" className="nav-pill-primary" style={{ padding: '0.75rem 2rem' }}>Back to Platform</Link>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', paddingTop: '5rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '3rem 1.5rem' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#64748b', fontSize: '0.85rem', marginBottom: '2rem', transition: 'color 0.2s' }}
          onMouseOver={e => e.currentTarget.style.color = 'var(--primary-gold)'}
          onMouseOut={e => e.currentTarget.style.color = '#64748b'}
        ><ArrowLeft size={16} />Back to Platform</Link>
 
        {/* Profile header */}
        <div className="glass-card" style={{ padding: '2.5rem', borderRadius: '1.5rem', marginBottom: '3rem', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative', overflow: 'hidden', border: '1px solid rgba(147, 51, 234, 0.12)', boxShadow: '0 4px 20px rgba(147, 51, 234, 0.04)' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(135deg,var(--primary-gold),var(--primary-gold-deep))' }} />
          <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
            <div style={{ width: '7rem', height: '7rem', borderRadius: '50%', border: '2px solid rgba(192,132,252,0.3)', padding: '4px' }}>
              <img
                src={profile.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.full_name || id}`}
                alt={profile.full_name || 'Scholar'}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            </div>
            <div style={{ position: 'absolute', bottom: '-8px', right: '-8px', background: 'linear-gradient(135deg,var(--primary-gold),var(--primary-gold-deep))', color: 'white', fontSize: '0.6rem', fontWeight: 700, padding: '0.25rem 0.625rem', borderRadius: '9999px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              {profile.role || 'Scholar'}
            </div>
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--navy-dark)', marginBottom: '0.5rem' }}>{profile.full_name || 'Scholar'}</h1>
          <p style={{ color: '#64748b', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3em', marginBottom: '2rem' }}>Ekalavya Certified Scholar</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', width: '100%', maxWidth: '360px' }}>
            {[{ icon: Zap, val: profile.score || 0, label: 'Growth Score', color: 'var(--primary-gold)' }, { icon: Flame, val: profile.streak || 0, label: 'Day Streak', color: '#fb923c' }].map(({ icon: Icon, val, label, color }) => (
              <div key={label} style={{ padding: '1.5rem', background: 'rgba(147, 51, 234, 0.04)', border: '1px solid rgba(147, 51, 234, 0.12)', borderRadius: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Icon size={20} color={color} fill={color} />
                  <span style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--navy-dark)' }}>{val}</span>
                </div>
                <p style={{ fontSize: '0.625rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.1em' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
 
        {/* Badges */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy-dark)' }}>Earned Badges</h2>
            <div style={{ flex: 1, height: '1px', background: 'rgba(0,0,0,0.08)' }} />
          </div>
          {profile.badges && profile.badges.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1.5rem' }}>
              {profile.badges.map((badge, i) => (
                <div key={i} className="glass-card" style={{ padding: '1.5rem', border: '1px solid rgba(147,51,234,0.12)', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', transition: 'all 0.3s' }}
                  onMouseOver={e => { e.currentTarget.style.background = 'rgba(147,51,234,0.08)'; }}
                  onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; }}
                >
                  <div style={{ width: '3rem', height: '3rem', background: 'rgba(147,51,234,0.06)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem', border: '1px solid rgba(147,51,234,0.12)' }}>
                    <Award size={24} color="var(--primary-gold)" />
                  </div>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--navy-dark)', textAlign: 'center' }}>{badge}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', background: 'rgba(0,0,0,0.01)', border: '1px dashed rgba(0,0,0,0.1)', borderRadius: '1rem' }}>
              <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>No badges earned yet. The journey has just begun.</p>
            </div>
          )}
        </div>
 
        {/* CTA */}
        <div style={{ textAlign: 'center', paddingTop: '2rem', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Inspired by this scholar's journey?</p>
          <Link to="/" className="nav-pill-primary" style={{ padding: '0.75rem 2.5rem', fontSize: '0.9rem' }}>Start Your Journey</Link>
        </div>
      </div>
    </div>
  );
}
